/**
 * `GET /api/history` — the local catalog, and nothing upstream.
 *
 * The server reads SQLite and returns immediately, kicking a background
 * refresh rather than waiting on one. `refreshing` is how many unfinished
 * local sessions remain inside the server's bounded reconciliation window —
 * not how many requests or workers are running at that instant. It is the
 * polling gate: this screen re-asks while any row may still change and stops
 * once every in-window row is final.
 *
 * ## There used to be a second hook here, and its deletion is the point
 *
 * `useRoomPeople` fetched a row's full detail on hover, because the only way
 * to learn WHO was on a call was to ask about that call specifically
 * (FINDINGS.md #13). `SessionSummary` now carries `participants_preview` and
 * `recording_count`, so every row arrives already knowing its own people — one
 * `GET /v1/sessions` for the whole list, made server-side, instead of one
 * request per row the pointer happened to cross. The hook is gone rather than
 * kept "for the prefetch": a request that exists to warm a cache is still a
 * request per hovered row, and the thing it warmed is no longer on the
 * critical path of anything the list draws.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import type { HistoryResponse } from '@era/shared'
import { ApiError, api } from '@/lib/api'
import { useLiveUpdates } from '@/lib/live'
import { keys } from '@/lib/query'

/**
 * The cadence when nothing is pushing.
 *
 * Unchanged from before `/api/events` existed, because this is what the screen
 * falls back to and it should fall back to something that worked.
 */
const POLL_MS = 8_000

/**
 * The cadence when the stream is up.
 *
 * Sixty seconds, not zero. `useLiveUpdates` invalidates this query the instant
 * a delivery is applied, so the poll is no longer how the list learns
 * anything — it is what catches the cases a push structurally cannot cover:
 * a room created before webhooks existed (frozen at creation, no callback,
 * will never emit an event), a delivery that exhausted its retry budget while
 * this server was being deployed, and a stream that a corporate proxy is
 * buffering into silence while reporting itself connected. The full argument
 * is in `lib/live.ts`.
 *
 * Note what it costs: `GET /api/history` returns from one local SQLite scan;
 * the handler also starts one fire-and-forget summary-list reconciliation for
 * the whole catalog. It does not run a detail request per unfinished row. A
 * minute is generous.
 */
const BACKSTOP_MS = 60_000

export function useHistory(): UseQueryResult<HistoryResponse, ApiError> {
  const live = useLiveUpdates()

  return useQuery<HistoryResponse, ApiError>({
    queryKey: keys.sessions,
    queryFn: () => api<HistoryResponse>('/api/history'),
    // `refreshing > 0` is still the gate: it means at least one in-window row
    // is unfinished, not that this many requests are simultaneously in
    // flight. The stream only changes how often we look while that is true.
    refetchInterval: (query) =>
      (query.state.data?.refreshing ?? 0) > 0 ? (live ? BACKSTOP_MS : POLL_MS) : false,
    staleTime: 3_000,
  })
}
